
import AccessibilityPanel from '@/components/ui/AccessibilityPanel';
import { Button } from '@/components/ui/button';
import QuickActions from '@/components/ui/QuickActions';
import SkipLink from '@/components/ui/SkipLink';
import { useAccessibility } from '@/components/ui/use-accessibility';
import { NotificationBell } from '@/components/quick-wins';
import { useAuth } from '@/hooks/useAuth';
import { useEntranceAnimation } from '@/hooks/useMicrointeractions';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { Menu } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import BottomTabBar from './BottomTabBar';
import MainHeader from './MainHeader';
import Sidebar from './Sidebar';

const AppLayout = () => {
  const { isOnline, syncOfflineData, hasOfflineData, isSyncing, isCacheValid } = useOfflineCache();
  const { keyboardNavigation } = useAccessibility();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [accessibilityPanelOpen, setAccessibilityPanelOpen] = useState(false);
  const hasInitialSyncRef = useRef<boolean>(false);
  const location = useLocation();
  const { isVisible, getEntranceClasses } = useEntranceAnimation(100);
  const [syncError, setSyncError] = useState(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user, loading: authLoading } = useAuth();

  // Determinar qual sidebar usar baseado na rota
  // Sidebar de Prestador apenas para rotas de gestão do prestador (não para perfil público)
  const SidebarComponent = Sidebar;

  // Desativar logs/efeitos de cliente nas rotas de prestador
  const isPrestadorRoute = location.pathname.startsWith('/prestador') && !location.pathname.startsWith('/prestador/perfil');

  // Detectar tamanho da tela
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Atalhos de teclado globais
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt + A para abrir painel de acessibilidade
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setAccessibilityPanelOpen(true);
      }
      
      // Alt + H para ir para home
      if (e.altKey && e.key === 'h') {
        e.preventDefault();
        window.location.href = '/dashboard';
      }

      // Alt + M para abrir/fechar menu
      if (e.altKey && e.key === 'm') {
        e.preventDefault();
        setSidebarOpen(!sidebarOpen);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setAccessibilityPanelOpen, sidebarOpen]);

  // Sincronização inicial — só quando autenticado e online
  useEffect(() => {
    if (isOnline && !hasInitialSyncRef.current && !isCacheValid && user && !authLoading) {
      hasInitialSyncRef.current = true;
      syncOfflineData();
    }
  }, [isOnline, isCacheValid, syncOfflineData, user, authLoading]);

  // Removido toast de sincronização para evitar duplicidade com `useOfflineCache`
  // Mantemos feedback visual via indicador de sincronização e estados de loading

  // Timeout para sincronização
  useEffect(() => {
    if (isSyncing) {
      setSyncError(false);
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(() => {
        setSyncError(true);
      }, 12000); // 12s — sync interno tem timeout de 10s
    } else {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      setSyncError(false);
    }
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [isSyncing]);

  // Configurar breadcrumbs baseado na rota
  const getBreadcrumbItems = () => {
    const pathMap: Record<string, string> = {
      dashboard: 'Dashboard',
      explore: 'Explorar',
      appointments: 'Agendamentos',
      messages: 'Mensagens',
      profile: 'Perfil',
      avaliacoes: 'Avaliações',
      atividade: 'Atividade',
      servicos: 'Serviços',
      prestadores: 'Prestadores',
      resultados: 'Resultados'
    };

    const segments = location.pathname.split('/').filter(Boolean);
    return segments.map(segment => ({
      label: pathMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
      href: segment === segments[segments.length - 1] ? undefined : `/${segment}`
    }));
  };

  // Removido retorno antecipado: mesmo sem login, mantemos layout com sidebar fixo

  const SidebarSkeleton = useMemo(() => (
    <div className="w-64 h-full bg-white dark:bg-slate-900 shadow-lg border-r border-slate-100 dark:border-slate-800 px-6 py-8 space-y-4 animate-pulse">
      <div className="h-10 bg-slate-200 rounded-lg" />
      <div className="h-6 bg-slate-200 rounded" />
      <div className="h-6 bg-slate-200 rounded" />
      <div className="h-6 bg-slate-200 rounded" />
      <div className="h-6 bg-slate-200 rounded" />
    </div>
  ), []);

  const MainSkeleton = useMemo(() => (
    <div className="space-y-6 animate-pulse" role="status" aria-live="polite">
      <div className="h-8 w-1/3 max-w-sm bg-slate-200 rounded-lg" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="h-40 bg-white/80 dark:bg-slate-900/70 rounded-2xl shadow-inner" />
        <div className="h-40 bg-white/80 dark:bg-slate-900/70 rounded-2xl shadow-inner" />
      </div>
      <div className="h-64 bg-white/80 dark:bg-slate-900/70 rounded-3xl shadow-inner" />
    </div>
  ), []);

  const shouldShowSidebar = !isMobile && (user || authLoading);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 flex">
      {/* Skip Links para acessibilidade */}
      <SkipLink href="#main-content">Pular para o conteúdo principal</SkipLink>
      <SkipLink href="#navigation">Pular para a navegação</SkipLink>
      
      {/* Sidebar para Desktop – apenas para usuários autenticados */}
      {shouldShowSidebar && (
        <nav id="navigation" role="navigation" aria-label="Navegação principal" className="flex-shrink-0">
          {authLoading ? SidebarSkeleton : <SidebarComponent />}
        </nav>
      )}

      {/* Overlay para Mobile */}
      {isMobile && sidebarOpen && user && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Mobile */}
      {isMobile && (user || authLoading) && (
        <nav 
          id="navigation" 
          role="navigation" 
          aria-label="Navegação principal"
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 shadow-lg transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {authLoading ? SidebarSkeleton : <SidebarComponent onClose={() => setSidebarOpen(false)} />}
        </nav>
      )}

      {/* Conteúdo Principal */}
      <div className="flex-1 flex flex-col">
        {/* Header: só exibe MainHeader na landing page (/) */}
        {location.pathname === '/' && <MainHeader />}

        {/* Header mobile (dentro da app): hamburger → sidebar lateral */}
        {isMobile && user && !authLoading && location.pathname !== '/' && (
          <header role="banner" className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen((prev) => !prev)}
                className="p-2"
                aria-label="Abrir menu de navegação"
                aria-expanded={sidebarOpen}
              >
                <Menu className="w-6 h-6 text-slate-700 dark:text-slate-200" aria-hidden="true" />
              </Button>
              <span className="font-bold text-slate-900 dark:text-slate-100">TaMoAki</span>
            </div>
            <NotificationBell />
          </header>
        )}

        {/* Breadcrumbs removidos para interface mais limpa */}

        {/* Conteúdo Principal - Layout Compacto */}
        <main 
          id="main-content"
          role="main"
          className={`flex-1 overflow-auto pb-20 transition-all duration-500 ${getEntranceClasses()}`}
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            {syncError && (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3" role="alert" aria-live="polite">
                <span className="text-red-600 dark:text-red-300 font-medium text-sm">Erro ao sincronizar dados.</span>
                <div className="flex gap-2 ml-auto">
                  <Button size="sm" onClick={() => { setSyncError(false); syncOfflineData(); }} className="bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs">Tentar Novamente</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSyncError(false)}>Dispensar</Button>
                </div>
              </div>
            )}
            {authLoading ? MainSkeleton : <Outlet />}
          </div>
        </main>

        {/* Bottom Navigation */}
        {isMobile && user && !authLoading && <BottomTabBar />}
      </div>

      {/* Quick Actions */}
      <QuickActions />

      {/* Painel de Acessibilidade */}
      <AccessibilityPanel 
        isOpen={accessibilityPanelOpen}
        onClose={() => setAccessibilityPanelOpen(false)}
      />

      {/* Indicador de sincronização */}
      {isSyncing && (
        <div className="fixed bottom-4 right-4 bg-tamoaki-primary text-white px-4 py-2 rounded-lg shadow-medium z-50">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Sincronizando...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLayout;
